import { supabase } from '@/lib/supabaseClient';

/**
 * MST-1.1. The milestone catalogue, and the stage mapping that feeds it.
 *
 * ⚠️ THE RULE LIVES IN `lib/milestones.ts`, NOT HERE. Whether a project reached
 * a milestone is a pure function with its own tests, so it can be exercised
 * without a database. This module moves rows.
 *
 * ⚠️ `tenant_id` IS NEVER PASSED AS A FILTER. Every policy on these tables
 * resolves it with `get_current_user_tenant_id()`. A caller-supplied filter is
 * not a filter (F-1). It IS passed on INSERT, because a row has to carry one,
 * and the policy's WITH CHECK verifies it.
 */

export interface Milestone {
  id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
}

/**
 * The account's milestones, in the order they were arranged.
 *
 * `includeInactive` exists because a retired milestone must still be nameable
 * where a project reached it — deactivating hides it from the pickers, it does
 * not erase history.
 */
export async function fetchMilestones(includeInactive = false): Promise<Milestone[]> {
  let query = supabase
    .from('milestones')
    .select('id, name, description, sort_order, is_active')
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });

  if (!includeInactive) query = query.eq('is_active', true);

  const { data, error } = await query;
  if (error) {
    console.warn('[milestones] read failed', error);
    return [];
  }
  return (data ?? []) as Milestone[];
}

export async function createMilestone(
  tenantId: string,
  name: string,
  description: string | null,
  sortOrder: number,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase.from('milestones').insert({
    tenant_id: tenantId,
    name: name.trim(),
    description: description?.trim() || null,
    sort_order: sortOrder,
  });

  if (error) {
    // The unique constraint is per account, so this is the likely failure and
    // deserves a sentence rather than a Postgres code.
    if (error.code === '23505') {
      return { ok: false, error: `A milestone called "${name.trim()}" already exists.` };
    }
    console.warn('[milestones] create failed', error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export async function updateMilestone(
  id: string,
  patch: Partial<Pick<Milestone, 'name' | 'description' | 'sort_order' | 'is_active'>>,
): Promise<{ ok: boolean; error?: string }> {
  const { error } = await supabase
    .from('milestones')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    if (error.code === '23505') {
      return { ok: false, error: 'A milestone with that name already exists.' };
    }
    console.warn('[milestones] update failed', error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/**
 * Retire a milestone.
 *
 * ⚠️ DEACTIVATES RATHER THAN DELETES, and the caller is not offered a delete.
 * Deleting would set `stages.milestone_id` to NULL by the foreign key AND cascade
 * away every override row, silently destroying the record of who asserted what.
 * Retiring keeps both and only hides the milestone from the pickers.
 */
export async function retireMilestone(id: string): Promise<{ ok: boolean; error?: string }> {
  return updateMilestone(id, { is_active: false });
}

/** How many stages point at each milestone. Drives "nothing maps to this yet". */
export async function fetchStageCountsByMilestone(): Promise<Map<string, number>> {
  const counts = new Map<string, number>();

  const { data, error } = await supabase
    .from('stages')
    .select('milestone_id')
    .not('milestone_id', 'is', null);

  if (error) {
    console.warn('[milestones] stage count read failed', error);
    return counts;
  }

  for (const row of (data ?? []) as { milestone_id: string }[]) {
    counts.set(row.milestone_id, (counts.get(row.milestone_id) ?? 0) + 1);
  }
  return counts;
}
